import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Invoices from './pages/Invoices';
import CreateInvoice from './pages/CreateInvoice';
import ViewInvoice from './pages/ViewInvoice';
import Customers from './pages/Customers';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import Products from './pages/Products';
import EditInvoice from './pages/EditInvoice';
import './App.css';
import './modern-enhancements.css';
import Quotations from './pages/Quotations';
import CreateQuotation from './pages/CreateQuotation';
import ViewQuotation from './pages/ViewQuotation';
import EditQuotation from './pages/EditQuotation';
import CreateProfessionalQuotation from './pages/CreateProfessionalQuotation';
import ViewProfessionalQuotation from './pages/ViewProfessionalQuotation';
import EditProfessionalQuotation from './pages/EditProfessionalQuotation';
import ProjectAssurance from './pages/ProjectAssurance';

import { HelmetProvider } from 'react-helmet-async';

import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import FundsPolicy from './pages/FundsPolicy';

import ScrollToTop from './components/ScrollToTop';

import AboutUs from './pages/AboutUs';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Careers from './pages/Careers';
import Contact from './pages/Contact';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminPayments from './pages/admin/AdminPayments';

// Admin private route guard
const AdminPrivateRoute = ({ children }) => {
  const { admin, loading } = useAdminAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  return (
    <HelmetProvider>
      <AdminAuthProvider>
        <AuthProvider>
          <Router>
            <ScrollToTop />
            <div className="App">
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                
                {/* Public Company Pages */}
                <Route path="/about" element={<AboutUs />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/contact" element={<Contact />} />
                
                {/* Public Legal Pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsAndConditions />} />
                <Route path="/refund-policy" element={<FundsPolicy />} />

                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/invoice" element={<PrivateRoute><Invoices /></PrivateRoute>} />
                <Route path="/invoice/create" element={<PrivateRoute><CreateInvoice /></PrivateRoute>} />
                <Route path="/invoice/:id/edit" element={<PrivateRoute><EditInvoice /></PrivateRoute>} />
                <Route path="/invoice/:id" element={<PrivateRoute><ViewInvoice /></PrivateRoute>} />
                <Route path="/quotations" element={<PrivateRoute><Quotations /></PrivateRoute>} />
                <Route path="/quotations/create" element={<PrivateRoute><CreateQuotation /></PrivateRoute>} />
                <Route path="/quotations/:id/edit" element={<PrivateRoute><EditQuotation /></PrivateRoute>} />
                <Route path="/quotations/:id" element={<PrivateRoute><ViewQuotation /></PrivateRoute>} />
                <Route path="/project-assurance" element={<PrivateRoute><ProjectAssurance /></PrivateRoute>} />
                <Route path="/professional-quotations/create" element={<PrivateRoute><CreateProfessionalQuotation /></PrivateRoute>} />
                <Route path="/professional-quotations/:id/edit" element={<PrivateRoute><EditProfessionalQuotation /></PrivateRoute>} />
                <Route path="/professional-quotations/:id" element={<PrivateRoute><ViewProfessionalQuotation /></PrivateRoute>} />
                <Route path="/customers" element={<PrivateRoute><Customers /></PrivateRoute>} />
                <Route path="/products" element={<PrivateRoute><Products /></PrivateRoute>} />
                <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />

                {/* ─── Admin Routes ──────────────────────────── */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminPrivateRoute><AdminDashboard /></AdminPrivateRoute>} />
                <Route path="/admin/users" element={<AdminPrivateRoute><AdminUsers /></AdminPrivateRoute>} />
                <Route path="/admin/payments" element={<AdminPrivateRoute><AdminPayments /></AdminPrivateRoute>} />
                <Route path="/admin" element={<Navigate to="/admin/login" replace />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </AdminAuthProvider>
    </HelmetProvider>
  );
}

export default App;

